import { QueryClient, QueryKey } from '@tanstack/react-query';

import { Notifications } from '@/api/Notifications';
import { NotificationRequest, NotificationResponse } from '@/api/data-contracts';
import { queryKeys } from '@/react-query/constants';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

export interface NotificationCountBreakdown {
  critical: number;
  important: number;
  common: number;
}

type QuerySnapshot = [QueryKey, unknown][];

export interface NotificationsCacheSnapshot {
  notifications: QuerySnapshot;
  notificationsCounts: QuerySnapshot;
  notificationsCountBreakdown: NotificationCountBreakdown | undefined;
}

const emptyBreakdown: NotificationCountBreakdown = {
  critical: 0,
  important: 0,
  common: 0,
};

const isInfiniteNotificationData = (
  data: unknown,
): data is { pages: NotificationResponse[][]; pageParams: unknown[] } => {
  return (
    !!data &&
    typeof data === 'object' &&
    Array.isArray((data as { pages?: unknown }).pages) &&
    Array.isArray((data as { pageParams?: unknown }).pageParams)
  );
};

const isNotificationsQuery = (queryKey: QueryKey) =>
  Array.isArray(queryKey) && queryKey[0] === queryKeys.notifications;

const markNotificationRead = <T extends NotificationResponse>(notification: T): T => ({
  ...notification,
  status: 'READ',
});

const markMatchingNotificationsRead = <T extends NotificationResponse>(
  notifications: T[],
  notificationIds: string[],
) => {
  const notificationIdSet = new Set(notificationIds);

  return notifications.map((notification) =>
    notificationIdSet.has(notification.id) ? markNotificationRead(notification) : notification,
  );
};

const filterMatchingNotifications = <T extends NotificationResponse>(
  notifications: T[],
  notificationIds: string[],
) => {
  const notificationIdSet = new Set(notificationIds);

  return notifications.filter((notification) => !notificationIdSet.has(notification.id));
};

const isNotificationsCountQuery = (queryKey: QueryKey) =>
  Array.isArray(queryKey) && queryKey[0] === queryKeys.notificationsCount;

const setAllNotificationsCountQueries = (queryClient: QueryClient, count: number) => {
  queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isNotificationsCountQuery(query.queryKey) })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, count);
    });
};

const decrementAllNotificationsCountQueries = (queryClient: QueryClient, amount: number) => {
  queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isNotificationsCountQuery(query.queryKey) })
    .forEach((query) => {
      queryClient.setQueryData<number | undefined>(query.queryKey, (prev) =>
        Math.max(0, (prev ?? 0) - amount),
      );
    });
};

const incrementMatchingNotificationsCountQueries = (
  queryClient: QueryClient,
  targetModule?: string,
) => {
  queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isNotificationsCountQuery(query.queryKey) })
    .forEach((query) => {
      const queryTargetModule = query.queryKey[1];
      const matchesTarget =
        queryTargetModule === undefined ||
        queryTargetModule === 'all-modules' ||
        queryTargetModule === targetModule;

      if (!matchesTarget) return;

      queryClient.setQueryData<number | undefined>(query.queryKey, (prev) => (prev ?? 0) + 1);
    });
};

const snapshotNotificationsCache = (queryClient: QueryClient): NotificationsCacheSnapshot => ({
  notifications: queryClient.getQueriesData({ queryKey: [queryKeys.notifications] }),
  notificationsCounts: queryClient.getQueriesData({ queryKey: [queryKeys.notificationsCount] }),
  notificationsCountBreakdown: queryClient.getQueryData([queryKeys.notificationsCountBreakdown]),
});

export const optimisticallyMarkAllNotificationsAsRead = (
  queryClient: QueryClient,
): NotificationsCacheSnapshot => {
  const snapshot = snapshotNotificationsCache(queryClient);

  queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isNotificationsQuery(query.queryKey) })
    .forEach((query) => {
      const queryKey = query.queryKey;
      const listMode = queryKey[1];

      queryClient.setQueryData(queryKey, (current: unknown) => {
        if (!isInfiniteNotificationData(current)) return current;

        if (listMode === 'unread') {
          return {
            ...current,
            pages: [[]],
            pageParams: current.pageParams.length ? [current.pageParams[0]] : [0],
          };
        }

        if (listMode === 'all') {
          return {
            ...current,
            pages: current.pages.map((page) => page.map(markNotificationRead)),
          };
        }

        return current;
      });
    });

  setAllNotificationsCountQueries(queryClient, 0);
  queryClient.setQueryData([queryKeys.notificationsCountBreakdown], emptyBreakdown);

  return snapshot;
};

export const optimisticallyMarkNotificationsAsRead = (
  queryClient: QueryClient,
  notificationIds: string[],
): NotificationsCacheSnapshot => {
  const snapshot = snapshotNotificationsCache(queryClient);

  queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isNotificationsQuery(query.queryKey) })
    .forEach((query) => {
      const queryKey = query.queryKey;
      const listMode = queryKey[1];

      queryClient.setQueryData(queryKey, (current: unknown) => {
        if (!isInfiniteNotificationData(current)) return current;

        if (listMode === 'unread') {
          return {
            ...current,
            pages: current.pages.map((page) => filterMatchingNotifications(page, notificationIds)),
          };
        }

        if (listMode === 'all') {
          return {
            ...current,
            pages: current.pages.map((page) =>
              markMatchingNotificationsRead(page, notificationIds),
            ),
          };
        }

        return current;
      });
    });

  decrementAllNotificationsCountQueries(queryClient, notificationIds.length);

  return snapshot;
};

export const restoreNotificationsCacheSnapshot = (
  queryClient: QueryClient,
  snapshot?: NotificationsCacheSnapshot,
) => {
  if (!snapshot) return;

  snapshot.notifications.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
  snapshot.notificationsCounts.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
  queryClient.setQueryData(
    [queryKeys.notificationsCountBreakdown],
    snapshot.notificationsCountBreakdown,
  );
};

export const optimisticallyAddUnreadNotification = (
  queryClient: QueryClient,
  notification: NotificationResponse,
  targetModule?: string,
): boolean => {
  if (notification.status !== 'UNREAD') return false;
  if (
    targetModule &&
    notification.targetModule !== targetModule &&
    notification.targetModule !== 'ALL' &&
    !notification.targetModule?.includes(targetModule)
  ) {
    return false;
  }

  const addToFirstPage = (current: unknown) => {
    if (!isInfiniteNotificationData(current)) {
      return { pages: [[notification]], pageParams: [0] };
    }

    const alreadyExists = current.pages.some((page) =>
      page.some((item) => item.id === notification.id),
    );
    if (alreadyExists) return current;

    const [firstPage = [], ...restPages] = current.pages;
    return {
      ...current,
      pages: [[notification, ...firstPage], ...restPages],
    };
  };

  queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isNotificationsQuery(query.queryKey) })
    .forEach((query) => {
      const queryKey = query.queryKey;
      const listMode = queryKey[1];
      const queryTargetModule = queryKey[2];

      if (queryTargetModule !== (targetModule ?? 'all-modules')) return;
      if (listMode !== 'unread' && listMode !== 'all') return;

      queryClient.setQueryData(queryKey, addToFirstPage);
    });

  incrementMatchingNotificationsCountQueries(queryClient, targetModule);

  return true;
};

const useNotificationsService = () => {
  const notificationsApi = useUploadProgressService(Notifications);

  const getNotifications = async (
    request: NotificationRequest,
    signal?: AbortSignal | undefined,
  ): Promise<NotificationResponse[]> => {
    const { data } = await notificationsApi.getNotificationsForUser(request, { signal });
    return data.content || [];
  };

  const getNotificationsCount = async (
    targetModule?: string,
    signal?: AbortSignal,
  ): Promise<number> => {
    const { data } = await notificationsApi.getUnreadNotificationsCountForUser(
      { unreadOnly: true, ...(targetModule ? { targetModule } : {}) },
      { signal },
    );
    return data;
  };

  const getNotificationsCountBreakdown = async (
    signal: AbortSignal | undefined,
    targetModule?: string,
  ): Promise<NotificationCountBreakdown> => {
    const { data } = await notificationsApi.getUnreadNotificationsCountBreakdown(
      { unreadOnly: true, ...(targetModule ? { targetModule } : {}) },
      { signal },
    );
    return data;
  };

  const markNotificationsAsRead = async (notificationIds: string[]): Promise<void> => {
    await notificationsApi.markNotificationsAsRead({ notificationIds });
  };

  const markAllNotificationsAsRead = async (): Promise<void> => {
    // @ts-ignore: body is intentionally passed to set Content-Type: application/json required by the endpoint
    await notificationsApi.markAllNotificationsAsRead({ body: {} });
  };

  return {
    getNotifications,
    getNotificationsCount,
    getNotificationsCountBreakdown,
    markNotificationsAsRead,
    markAllNotificationsAsRead,
  };
};

export default useNotificationsService;
